import React, { useState } from 'react';
import { ChevronRight, ChevronDown, Users } from 'lucide-react';

/** Remitentes únicos del período. Los que enviaron más de una vez se despliegan al hacer clic. */
export const RemitentesTabla = ({ remitentes, loading, total, page, pages, onPage, formatMonto, children }) => {
  const [abiertos, setAbiertos] = useState(() => new Set());

  const toggle = (clave) => setAbiertos(prev => {
    const next = new Set(prev);
    next.has(clave) ? next.delete(clave) : next.add(clave);
    return next;
  });

  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
      <div className="p-5 pb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Users className="w-4 h-4 text-violet-600 dark:text-violet-400" />
            Remitentes del período
          </h3>
          <p className="text-xs text-gray-500">
            {total.toLocaleString('es-AR')} remitentes únicos · clic en los que tienen más de un envío para ver el detalle
          </p>
        </div>
        {children}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-900/40">
            <tr className="text-[10px] uppercase tracking-wide text-gray-500">
              <th className="px-3 py-2 text-left w-8" />
              <th className="px-3 py-2 text-left">Titular</th>
              <th className="px-3 py-2 text-left">CUIL/DNI</th>
              <th className="px-3 py-2 text-center">Envíos</th>
              <th className="px-3 py-2 text-right">Total enviado</th>
              <th className="px-3 py-2 text-right">Promedio</th>
              <th className="px-3 py-2 text-left">Último envío</th>
              <th className="px-3 py-2 text-left">Club / Cartera</th>
            </tr>
          </thead>
          <tbody data-testid="remitentes-tbody">
            {loading ? (
              <tr><td colSpan="8" className="px-3 py-10 text-center text-sm text-gray-400">Cargando…</td></tr>
            ) : remitentes.length === 0 ? (
              <tr><td colSpan="8" className="px-3 py-10 text-center text-sm text-gray-400">No hay remitentes en este filtro.</td></tr>
            ) : remitentes.map((r) => {
              const clave = `${r.titular}|${r.cuil || ''}`;
              const varios = r.cantidad_envios > 1;
              const abierto = abiertos.has(clave);
              const Icon = abierto ? ChevronDown : ChevronRight;
              return (
                <React.Fragment key={clave}>
                  <tr onClick={() => varios && toggle(clave)}
                    className={`border-t border-gray-100 dark:border-gray-700 transition-colors ${varios
                      ? 'cursor-pointer hover:bg-violet-50/60 dark:hover:bg-violet-900/20'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'} ${abierto ? 'bg-violet-50/60 dark:bg-violet-900/20' : ''}`}
                    data-testid={`remitente-fila-${r.titular}`}>
                    <td className="px-3 py-2">
                      {varios && <Icon className="w-4 h-4 text-violet-600 dark:text-violet-400" />}
                    </td>
                    <td className="px-3 py-2 text-xs font-medium text-gray-900 dark:text-gray-100">
                      {r.titular || '—'}
                      {r.nombres_alternativos?.length > 0 && (
                        <span className="block text-[10px] font-normal text-gray-400 truncate max-w-[240px]"
                          title={r.nombres_alternativos.join(' · ')}>
                          también: {r.nombres_alternativos.join(' · ')}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500 font-mono">
                      {r.cuil || '—'}
                      {r.cuil_ambiguo && (
                        <span title="Este CUIL aparece con nombres de personas distintas (posible error de lectura). Se separó por nombre."
                          className="block text-[9px] text-amber-600 dark:text-amber-400 font-sans"
                          data-testid={`remitente-cuil-dudoso-${r.titular}`}>
                          ⚠ CUIL dudoso
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${varios
                        ? 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300'
                        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                        {r.cantidad_envios}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-right font-mono font-semibold text-emerald-700 dark:text-emerald-400">
                      {formatMonto(r.total_enviado)}
                    </td>
                    <td className="px-3 py-2 text-xs text-right font-mono text-gray-500">{formatMonto(r.promedio_envio)}</td>
                    <td className="px-3 py-2 text-xs font-mono text-gray-500">{r.ultimo_envio}</td>
                    <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
                      {r.clubes.slice(0, 2).join(', ') || '—'}
                      {r.clubes.length > 2 && <span className="text-gray-400"> y {r.clubes.length - 2} más</span>}
                      {r.clubes.length > 1 && (
                        <span className="ml-2 inline-block text-[9px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 font-semibold whitespace-nowrap">
                          {r.clubes.length} clubes
                        </span>
                      )}
                    </td>
                  </tr>
                  {abierto && (
                    <tr className="bg-gray-50/80 dark:bg-gray-900/40">
                      <td colSpan="8" className="px-3 py-2">
                        <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
                          data-testid={`remitente-envios-${r.titular}`}>
                          <table className="w-full">
                            <thead>
                              <tr className="text-[9px] uppercase tracking-wide text-gray-500 bg-white dark:bg-gray-800">
                                <th className="px-3 py-1.5 text-left">Fecha</th>
                                <th className="px-3 py-1.5 text-left">Hora</th>
                                <th className="px-3 py-1.5 text-right">Monto</th>
                                <th className="px-3 py-1.5 text-left">Banco</th>
                                <th className="px-3 py-1.5 text-left">Destinatario</th>
                                <th className="px-3 py-1.5 text-left">Club</th>
                                <th className="px-3 py-1.5 text-left">ID</th>
                              </tr>
                            </thead>
                            <tbody>
                              {r.envios.map((e, i) => (
                                <tr key={`${e.reconciliation_id || i}`}
                                  className="border-t border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
                                  <td className="px-3 py-1.5 text-[11px] font-mono text-gray-600 dark:text-gray-300">{e.date}</td>
                                  <td className="px-3 py-1.5 text-[11px] font-mono text-gray-400">{e.time || '—'}</td>
                                  <td className="px-3 py-1.5 text-[11px] text-right font-mono font-semibold text-emerald-700 dark:text-emerald-400">
                                    {formatMonto(e.amount)}
                                  </td>
                                  <td className="px-3 py-1.5 text-[11px] text-gray-500">{e.bank || '—'}</td>
                                  <td className="px-3 py-1.5 text-[11px] text-gray-500">{e.destination_holder || '—'}</td>
                                  <td className="px-3 py-1.5 text-[11px] text-gray-600 dark:text-gray-400">{e.cliente_name || '—'}</td>
                                  <td className="px-3 py-1.5 text-[11px] font-mono text-violet-600 dark:text-violet-400">{e.reconciliation_id || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500">
            {((page - 1) * 50) + 1} - {Math.min(page * 50, total)} de {total.toLocaleString('es-AR')} remitentes
          </p>
          <div className="flex items-center gap-2">
            <button onClick={() => onPage(Math.max(1, page - 1))} disabled={page === 1}
              className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700"
              data-testid="remitentes-anterior">Anterior</button>
            <span className="text-xs text-gray-500 tabular-nums">{page} / {pages}</span>
            <button onClick={() => onPage(Math.min(pages, page + 1))} disabled={page === pages}
              className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700"
              data-testid="remitentes-siguiente">Siguiente</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RemitentesTabla;
